import { useIsInViewport } from 'hooks/use-is-in-viewport'
import * as React from 'react'
import { tw } from 'utils/classnames'

import { Slot } from '../slot'
import { IconButton } from '../buttons/icon-button'
import { ChevronLeftIcon, ChevronRightIcon } from '../icons'
import { ArrowNarrowLeftIcon, ArrowNarrowRightIcon } from '../icons/editor'

const ScrollerContext = React.createContext<{
  ref?: React.RefObject<HTMLUListElement | null>
  firstElementRef?: React.RefObject<HTMLLIElement | null>
  lastElementRef?: React.RefObject<HTMLLIElement | null>
  prevPage(): void
  nextPage(): void
  viewAll: boolean
  setViewAll: React.Dispatch<React.SetStateAction<boolean>>
}>(null!)

function useScrollerContext() {
  return React.useContext(ScrollerContext)
}

type ScrollerVariant = 'dark' | 'light'

type ScrollerScrollAreaProps = React.ComponentPropsWithoutRef<'div'> & {
  gutter?: number
  gap?: number
  variant?: ScrollerVariant
  hasRoundedNavButtons?: boolean
  fadeOutFromBgColor?: string
}

function ScrollerScrollArea({
  children,
  gutter,
  gap = 8,
  className,
  variant = 'light',
  hasRoundedNavButtons = false,
  fadeOutFromBgColor,
  ...props
}: ScrollerScrollAreaProps) {
  const ref = useScrollerContext()?.ref
  const prevPage = useScrollerContext()?.prevPage
  const nextPage = useScrollerContext()?.nextPage
  const firstElementRef = useScrollerContext()?.firstElementRef
  const lastElementRef = useScrollerContext()?.lastElementRef
  const viewAll = useScrollerContext()?.viewAll

  const isLastPage = useIsInViewport(lastElementRef, false)
  const isFirstPage = useIsInViewport(firstElementRef, true)

  if (viewAll) {
    return (
      <ul
        style={{
          '--scroller-gutter': 'var(--gutter)',
        }}
        className="scroller-x-xs z-10 mx-1 flex h-full scroll-p-2 flex-wrap content-baseline items-stretch justify-between gap-2 overflow-y-scroll"
        ref={ref}
      >
        {children}
      </ul>
    )
  }

  return (
    <div
      {...props}
      className={tw('relative', className)}
      style={
        // oxlint-disable-next-line no-negated-condition
        gutter !== undefined
          ? {
              '--gutter': `${gutter}px`,
            }
          : {}
      }
    >
      {isFirstPage ? null : (
        <ScrollButton
          direction="prev"
          onClick={prevPage}
          variant={variant}
          isRounded={hasRoundedNavButtons}
          data-testid="scroller-prev"
          fadeOutFromBgColor={fadeOutFromBgColor}
        />
      )}
      <ul
        style={{
          '--scroller-gutter': 'var(--gutter)',
          '--gap': `${gap}px`,
        }}
        className="scroller-x-xs z-10 flex scroll-p-2 items-stretch gap-[--gap] overflow-y-hidden"
        ref={ref}
      >
        <li className="-mr-[--gap] h-full w-0 shrink-0" ref={firstElementRef} />
        {children}
        {/* last element needs a width of px otherwise it collapses */}
        <li className="-ml-[--gap] h-full w-px shrink-0" ref={lastElementRef} />
      </ul>
      {isLastPage ? null : (
        <ScrollButton
          direction="next"
          onClick={nextPage}
          variant={variant}
          isRounded={hasRoundedNavButtons}
          fadeOutFromBgColor={fadeOutFromBgColor}
        />
      )}
    </div>
  )
}

function ScrollButton({
  onClick,
  variant = 'light',
  isRounded = false,
  direction = 'prev',
  fadeOutFromBgColor,
}: {
  onClick: () => void
  variant?: ScrollerVariant
  isRounded?: boolean
  direction?: 'prev' | 'next'
  fadeOutFromBgColor?: string
}) {
  const isPrev = direction === 'prev'
  const ArrowIcon = isPrev ? ArrowNarrowLeftIcon : ArrowNarrowRightIcon
  const ChevronIcon = isPrev ? ChevronLeftIcon : ChevronRightIcon

  return (
    <>
      <div
        className={tw(
          'pointer-events-none absolute top-0 z-20 h-full',
          isPrev ? 'left-0 bg-gradient-to-r' : 'right-0 bg-gradient-to-l',
          isRounded ? 'from-service-white w-[101px] md:w-[202px]' : 'w-[40px]',
          !isRounded && variant === 'light' && 'from-gray-900',
          !isRounded && variant !== 'light' && 'from-gray-50',
          fadeOutFromBgColor ? fadeOutFromBgColor : '',
        )}
      />
      <button
        type="button"
        onClick={onClick}
        className={tw(
          'group absolute top-0 z-20 flex h-full items-center justify-center pb-[--scroller-size] outline-none focus-visible:outline',
          isPrev ? 'left-0' : 'right-0',
          isRounded ? 'w-[44px] focus:outline-none' : 'w-5',
        )}
        aria-label={isPrev ? 'Scroll left button' : 'Scroll right button'}
      >
        {isRounded ? (
          <div className="bg-service-white flex size-[44px] items-center justify-center rounded-full border border-gray-100">
            <ArrowIcon className="ease-smooth size-[24px] text-gray-600 transition group-hover:scale-125" />
          </div>
        ) : (
          <ChevronIcon
            className={tw(
              'ease-smooth size-[20px] transition group-hover:scale-125',
              variant === 'light' ? 'text-gray-300' : 'text-gray-600',
            )}
          />
        )}
      </button>
    </>
  )
}

function ScrollerScrollItem({
  className,
  asChild,
  ...props
}: WithAsChild<React.ComponentPropsWithoutRef<'li'>>) {
  const Comp = asChild ? Slot : 'li'

  return (
    <Comp
      {...props}
      className={tw('aspect-square w-[113px] flex-shrink-0 snap-center items-center', className)}
    />
  )
}

function ScrollerRoot({ ...props }: React.ComponentPropsWithoutRef<'div'>) {
  const ref = React.useRef<HTMLUListElement>(null)
  const firstElementRef = React.useRef<HTMLLIElement>(null)
  const lastElementRef = React.useRef<HTMLLIElement>(null)

  const [viewAll, setViewAll] = React.useState<boolean>(false)

  // we use data attributes so you can do a double or triple click to speed up the scrolling
  function prevPage() {
    const scrollWidth = ref.current?.clientWidth ?? 0
    const currentScroll = ref.current?.dataset.scroll
      ? // oxlint-disable-next-line radix
        parseInt(ref.current?.dataset.scroll)
      : (ref.current?.scrollLeft ?? 0)

    const nextScroll = currentScroll - scrollWidth
    ref.current?.scrollTo({ left: nextScroll, behavior: 'smooth' })
    // oxlint-disable-next-line unicorn/prefer-dom-node-dataset
    ref.current?.setAttribute('data-scroll', nextScroll.toString())
  }

  function nextPage() {
    const scrollWidth = ref.current?.clientWidth ?? 0
    const currentScroll = ref.current?.dataset.scroll
      ? // oxlint-disable-next-line radix
        parseInt(ref.current?.dataset.scroll)
      : (ref.current?.scrollLeft ?? 0)

    const nextScroll = currentScroll + scrollWidth
    ref.current?.scrollTo({ left: nextScroll, behavior: 'smooth' })
    // oxlint-disable-next-line unicorn/prefer-dom-node-dataset
    ref.current?.setAttribute('data-scroll', nextScroll.toString())
  }

  React.useEffect(() => {
    if (ref.current) {
      ref.current.scrollLeft = 0
    }
  }, [props.children])

  return (
    <ScrollerContext.Provider
      value={{ ref, firstElementRef, lastElementRef, prevPage, nextPage, viewAll, setViewAll }}
    >
      {viewAll ? (
        <div className="bg-gray-1000 rounded-4 absolute bottom-0 left-0 right-0 top-12 z-50 overflow-hidden">
          {props.children}
        </div>
      ) : (
        <div {...props} className={tw('relative flex flex-col gap-2', props.className)} />
      )}
    </ScrollerContext.Provider>
  )
}

function ScrollerHeader(props: React.ComponentPropsWithoutRef<'div'>) {
  const { viewAll, setViewAll } = useScrollerContext()
  return (
    <>
      {viewAll ? (
        <div className="ml-4 flex items-center gap-2">
          <IconButton className="size-[34px]" onClick={() => setViewAll(false)}>
            <ChevronLeftIcon />
          </IconButton>
          {props.children}
        </div>
      ) : (
        <div {...props} className={tw('flex flex-row justify-between', props.className)} />
      )}
    </>
  )
}

function ScrollerTitle(props: React.ComponentPropsWithoutRef<'p'>) {
  const { viewAll } = useScrollerContext()
  return (
    <p
      {...props}
      className={tw('text-sm text-gray-400', props.className, viewAll && 'text-xl text-gray-200')}
    />
  )
}

export const Scroller = {
  Root: ScrollerRoot,
  Header: ScrollerHeader,
  ScrollArea: ScrollerScrollArea,
  Title: ScrollerTitle,
  ScrollItem: ScrollerScrollItem,
}
